import { FC, JSX, useEffect, useRef, useState } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { convertDateTime } from "@app/shared/convertDateTime";
import { ExportTypeConverter, ReportFileTypeConverter, ReportStatusConverter } from "@app/reports/reportsModel";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESButton } from "@app/shared/qes-standards/Button";
import ExportService from "@app/exports/exportService";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { ExportVolumeSpecialtyGroupExceptionsModel } from "@app/exceptions/interfaces";

interface ExportVolumeSpecialtyGroupExceptionsData {
    [wafflesKeyColumnName]: string;
    reportName: JSX.Element | string;
    exportType: string;
    projectName: string;
    createdDateUtc: string;
    createdBy: string;
    reportStatus: string;
    fileFormat: string;
}

/* oxlint-disable sort-keys */
const exceptionsDownloadsColumnConfig: ColumnConfig = {
    reportName: {
        displayText: "Report Name",
        testId: "report-name",
    },
    exportType: {
        displayText: "Type",
        testId: "type",
    },
    projectName: {
        displayText: "Exception Count",
        testId: "project-name",
    },
    createdDateUtc: {
        displayText: "Date",
        testId: "date",
    },
    createdBy: {
        displayText: "Created By",
        testId: "created-by",
    },
    reportStatus: {
        displayText: "Status",
        testId: "status",
    },
    fileFormat: {
        displayText: "Format",
        testId: "format",
    }
    /* oxlint-enable sort-keys */
};

const ExceptionsDownload: FC = () => {

    const [exceptionsDownloadsToday, setExceptionsDownloadsToday] = useState<ExportVolumeSpecialtyGroupExceptionsData[]>([]);
    const [exceptionsDownloadsLast7, setExceptionsDownloadsLast7] = useState<ExportVolumeSpecialtyGroupExceptionsData[]>([]);
    const [exceptionsDownloadsLast30, setExceptionsDownloadsLast30] = useState<ExportVolumeSpecialtyGroupExceptionsData[]>([]);
    const exceptionsService = useRef(new ExceptionsService()).current;
    const titleSlug = 'exceptions-downloads';

    const downloadReport = (projectId: number, reportId: number) => {
        const exportService = new ExportService();
        const link = (!!projectId && projectId > 0) ?
            `${exportService.getProjectReportLink(projectId, reportId)}` :
            `${exportService.getReportLink(reportId)}`;
        authenticatedDownload(link);
    }

    const convertExportModelToRecord = (exportModel: ExportVolumeSpecialtyGroupExceptionsModel) => {

        const timestamp = convertDateTime(exportModel.createDateUtc).toLocaleString("en-US");

        /* oxlint-disable sort-keys */
        const tableRow = {
            key: exportModel.id.toString(),
            reportName: exportModel.reportStatus == 'complete' ? (
                <QESButton
                    qesType="secondary"
                    qesTestId={`${titleSlug}-report-name-download-${exportModel.id}`}
                    onClick={() => downloadReport(exportModel.projectId, exportModel.id)}>{exportModel.reportName}
                </QESButton>
            ) : exportModel.reportName,
            exportType: ExportTypeConverter[exportModel.exportType as keyof typeof ExportTypeConverter],
            projectName: exportModel.projectName,
            createdDateUtc: timestamp,
            createdBy: exportModel.sharedBy,
            reportStatus: ReportStatusConverter[exportModel.reportStatus as keyof typeof ReportStatusConverter],
            fileFormat: ReportFileTypeConverter[exportModel.fileType as keyof typeof ReportFileTypeConverter],
        };
        /* oxlint-enable sort-keys */

        return tableRow;
    }

    useEffect(() => {

        exceptionsService.getVolumeExceptionsDownloadReports().then((serviceResponse: ExportVolumeSpecialtyGroupExceptionsModel[]) => {

            const now = new Date();
            const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
            sevenDaysAgo.setHours(0, 0, 0, 0);
            const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
            thirtyDaysAgo.setHours(0, 0, 0, 0);
            const tempReportsToday: ExportVolumeSpecialtyGroupExceptionsData[] = [];
            const tempReportsLast7: ExportVolumeSpecialtyGroupExceptionsData[] = [];
            const tempReportsLast30: ExportVolumeSpecialtyGroupExceptionsData[] = [];

            serviceResponse.forEach((item) => {
                const reportTime = convertDateTime(item.createDateUtc);
                if (now.getDate() === reportTime.getDate() && now.getMonth() === reportTime.getMonth() && now.getFullYear() === reportTime.getFullYear()) {
                    tempReportsToday.push(convertExportModelToRecord(item));
                } else if (reportTime < now && reportTime > sevenDaysAgo) {
                    tempReportsLast7.push(convertExportModelToRecord(item));
                } else if (reportTime < sevenDaysAgo && reportTime > thirtyDaysAgo) {
                    tempReportsLast30.push(convertExportModelToRecord(item));
                }
            });

            setExceptionsDownloadsToday(tempReportsToday);
            setExceptionsDownloadsLast7(tempReportsLast7);
            setExceptionsDownloadsLast30(tempReportsLast30);

        });

    }, []);

    return (
        <div className="container mt-3">

            <AccordionContainer className="mt-3">
                <Accordion title="Today" defaultOpen={true} testId="volume-exceptions-downloads__today-accordion">
                    <Waffles<ExportVolumeSpecialtyGroupExceptionsData>
                        columns={exceptionsDownloadsColumnConfig}
                        data={exceptionsDownloadsToday}
                        fullWidth={true}
                        testId={`${titleSlug}-reports-today`}
                    />
                </Accordion>
            </AccordionContainer>

            <AccordionContainer className="mt-3">
                <Accordion title="Last 7 Days" testId="volume-exceptions-downloads__last-7-days-accordion">
                    <Waffles<ExportVolumeSpecialtyGroupExceptionsData>
                        columns={exceptionsDownloadsColumnConfig}
                        data={exceptionsDownloadsLast7}
                        fullWidth={true}
                        testId={`${titleSlug}-reports-last-7`}
                    />
                </Accordion>
            </AccordionContainer>

            <AccordionContainer className="mt-3">
                <Accordion title="Last 30 Days" testId="volume-exceptions-downloads__last-30-days-accordion">
                    <Waffles<ExportVolumeSpecialtyGroupExceptionsData>
                        columns={exceptionsDownloadsColumnConfig}
                        data={exceptionsDownloadsLast30}
                        fullWidth={true}
                        testId={`${titleSlug}-reports-last-30`}
                    />
                </Accordion>
            </AccordionContainer>
        </div>
    );
}

export default ExceptionsDownload;